/**
 * Helpers puros para idempotencia y reintento de jobs V2 de Colega IA.
 *
 * Diseño:
 *  - localStorage guarda UN objeto por clave global scoped por user_id:
 *      key   = "colega_ia_active_job"
 *      value = JSON.stringify({
 *        user_id,
 *        idempotency_key,   // UUID v4
 *        payload_fingerprint,
 *        job_id             // opcional; se llena tras respuesta POST OK
 *      })
 *  - resolveActiveIdempotency(currentUserId, currentFingerprint, storage):
 *      • Si no hay entrada → null.
 *      • Si user_id difiere del actual → descarta la entrada (limpia) y
 *        retorna null. Nunca reutiliza claves de otro usuario.
 *      • Si user_id coincide y fingerprint(payload) coincide → devuelve el
 *        objeto tal cual (permite reintento del MISMO envío).
 *      • Si user_id coincide pero fingerprint difiere → retorna null (fue
 *        un envío distinto que quedó huérfano; no lo reutilizamos).
 *  - beginSubmit(currentUserId, currentFingerprint, storage, uuidGen):
 *      • Reutiliza la entrada existente si aplica (idem UUID).
 *      • Si no aplica, genera UUID nuevo, persiste y devuelve la entrada.
 *  - persistJobId(storage, jobId): completa el job_id tras respuesta OK.
 *  - clearActiveJob(storage): al `completed` o `failed`, o al cambio de
 *    usuario. Idempotente.
 *
 * Las funciones son PURAS respecto a React (aceptan cualquier storage con
 * getItem/setItem/removeItem) para poder ser probadas sin renderizar UI.
 */

export const ACTIVE_JOB_STORAGE_KEY = "colega_ia_active_job";

/** Completed states and the done phase must never leave polling active. */
export function isTerminalJobDoc(jobDoc) {
  return ["completed", "completed_with_fallback", "failed"].includes(jobDoc?.status)
    || jobDoc?.phase === "done";
}

export function getInternalTestStatus(status) {
  if (status?.active !== true) return null;
  const limit = Number(status.limit);
  const remaining = Number(status.remaining);
  if (!Number.isFinite(limit) || !Number.isFinite(remaining)) return null;
  return { limit, remaining: Math.max(0, remaining) };
}

/** Genera una huella estable del payload del envío deliberado. */
export function fingerprintPayload(payload) {
  const p = payload || {};
  const rel = Array.isArray(p.related_legal_areas) ? p.related_legal_areas : [];
  const norm = {
    context: (p.context || "").trim(),
    mode: p.legal_area_mode || "automatic",
    primary: p.primary_legal_area || "",
    related: [...rel].sort().join(","),
    custom: (p.custom_legal_area || "").trim(),
  };
  // JSON estable (claves ordenadas por Object.keys en orden de inserción).
  return JSON.stringify(norm);
}

/** Lee y valida la entrada activa (no valida user_id todavía). */
function _readEntry(storage) {
  try {
    const raw = storage.getItem(ACTIVE_JOB_STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (!obj.user_id || !obj.idempotency_key) return null;
    return obj;
  } catch {
    return null;
  }
}

/**
 * Devuelve la entrada activa para reintento SOLO si user_id y fingerprint
 * coinciden. En otro caso limpia la entrada y devuelve null.
 */
export function resolveActiveIdempotency(currentUserId, currentFingerprint, storage) {
  const entry = _readEntry(storage);
  if (!entry) return null;
  if (entry.user_id !== currentUserId) {
    // Entrada pertenece a otro usuario — nunca reutilizar.
    storage.removeItem(ACTIVE_JOB_STORAGE_KEY);
    return null;
  }
  if (entry.payload_fingerprint !== currentFingerprint) return null;
  return entry;
}

/**
 * Devuelve la entrada activa recuperable tras refresh (misma cuenta),
 * SIN filtrar por fingerprint. Se usa cuando la app inicia y queremos
 * retomar el polling del job pendiente. Si la entrada pertenece a otra
 * cuenta, la borra y retorna null.
 */
export function resolveActiveJobForRestore(currentUserId, storage) {
  const entry = _readEntry(storage);
  if (!entry) return null;
  if (entry.user_id !== currentUserId) {
    storage.removeItem(ACTIVE_JOB_STORAGE_KEY);
    return null;
  }
  if (!entry.job_id) return null;  // sin job_id aún → nada que retomar
  return entry;
}

/**
 * Inicia (o reintenta) un envío deliberado:
 *  - Si hay entrada activa del mismo usuario Y mismo fingerprint → REUSA el
 *    UUID (reintento del mismo envío tras error de red).
 *  - Si no, genera un UUID nuevo, persiste y devuelve.
 */
export function beginSubmit(currentUserId, currentFingerprint, storage, uuidGen) {
  const existing = resolveActiveIdempotency(currentUserId, currentFingerprint, storage);
  if (existing) return existing;
  const entry = {
    user_id: currentUserId,
    idempotency_key: uuidGen(),
    payload_fingerprint: currentFingerprint,
    job_id: null,
  };
  storage.setItem(ACTIVE_JOB_STORAGE_KEY, JSON.stringify(entry));
  return entry;
}

/** Completa el job_id de la entrada tras respuesta POST OK. No-op si la
 *  entrada ya no existe o pertenece a otro usuario. */
export function persistJobId(currentUserId, jobId, storage) {
  const entry = _readEntry(storage);
  if (!entry) return false;
  if (entry.user_id !== currentUserId) return false;
  entry.job_id = jobId;
  storage.setItem(ACTIVE_JOB_STORAGE_KEY, JSON.stringify(entry));
  return true;
}

/** Elimina la entrada activa. Idempotente. */
export function clearActiveJob(storage) {
  try { storage.removeItem(ACTIVE_JOB_STORAGE_KEY); } catch { /* noop */ }
}

/** Wrapper: UUID v4 con fallback conservador si crypto.randomUUID no existe. */
export function defaultUuidGen() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return "idem-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}
